/**
 * Public credential verification page — FR-I-1.
 *
 * Routed at /verify/:credentialId. In production the Cloudflare Pages
 * deploy serves this hostname-rewrite from verify.cait.academy/<uuid>,
 * so a verifier scanning the cert's QR code or pasting the printed URL
 * lands here.
 *
 * Anonymous — no auth. Renders the public-safe payload returned by
 * GET /api/verify/{id} as a university-style certificate artifact on
 * top, with verification metadata + actions below it.
 *
 * Status rendering (FR-H-5, FR-I-1):
 *   - active  → green check, "currently valid"
 *   - expired → amber, "expired on <date>; ask the holder to recertify"
 *   - lapsed  → amber, similar to expired with stronger language
 *   - revoked → red, "this credential was revoked"
 */
import type * as React from "react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { ApiError } from "@/lib/api";
import { fetchCredential, type VerifyPayload } from "@/lib/verify-api";

type LoadState =
  | { kind: "loading" }
  | { kind: "loaded"; payload: VerifyPayload }
  | { kind: "not_found" }
  | { kind: "error"; message: string };

// FR-H-2: the canonical cert URL is verify.cait.academy/<credential_id>
// — a bare UUID, no /verify/ prefix. We accept either /<uuid> or
// /verify/<uuid> in the SPA router so both QR-scan and direct-link
// paths land on this page. Non-UUID paths fall through to the
// not_found state instead of crashing the SPA.
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

interface StatusBadge {
  label: string;
  pillClass: string;
  description: string;
}

function statusBadge(payload: VerifyPayload): StatusBadge {
  if (payload.is_currently_valid) {
    return {
      label: "Active",
      pillClass: "bg-emerald-100 text-emerald-900 border-emerald-300",
      description: "This credential is currently valid.",
    };
  }
  if (payload.status === "expired" || payload.status === "lapsed") {
    return {
      label: payload.status === "expired" ? "Expired" : "Lapsed",
      pillClass: "bg-amber-100 text-amber-900 border-amber-300",
      description: `Expired on ${formatDate(payload.expires_at)}. Ask the holder to recertify.`,
    };
  }
  if (payload.status === "revoked") {
    return {
      label: "Revoked",
      pillClass: "bg-rose-100 text-rose-900 border-rose-300",
      description: "This credential was revoked and is no longer valid.",
    };
  }
  return {
    label: "Unknown",
    pillClass: "bg-muted text-foreground border-border",
    description: "This credential's status is not recognized.",
  };
}

/** Reusable circular seal that matches the PDF's seal. */
function CaitSeal({ size = 96 }: { size?: number }): React.JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 120 120" aria-hidden="true">
      <circle cx="60" cy="60" r="56" fill="none" stroke="#0B2545" strokeWidth="2" />
      <circle cx="60" cy="60" r="50" fill="none" stroke="#C9A86B" strokeWidth="1.2" />
      <circle cx="60" cy="60" r="34" fill="#FBF8F1" stroke="#0B2545" strokeWidth="1" />
      <text
        x="60"
        y="56"
        textAnchor="middle"
        fontFamily="Georgia, serif"
        fontSize="20"
        fontWeight={700}
        fill="#0B2545"
      >
        CaiT
      </text>
      <text
        x="60"
        y="74"
        textAnchor="middle"
        fontFamily="Helvetica, Arial, sans-serif"
        fontSize="6.5"
        letterSpacing="2"
        fill="#0B2545"
      >
        ACADEMY
      </text>
      <path d="M28 60 Q32 50 38 46 Q34 56 32 64 Q28 60 28 60 Z" fill="#C9A86B" />
      <path d="M92 60 Q88 50 82 46 Q86 56 88 64 Q92 60 92 60 Z" fill="#C9A86B" />
      <path d="M16 94 L60 102 L104 94 L96 110 L60 116 L24 110 Z" fill="#0B2545" />
      <text
        x="60"
        y="110"
        textAnchor="middle"
        fontFamily="Helvetica, Arial, sans-serif"
        fontSize="6"
        letterSpacing="2.5"
        fill="#FBF8F1"
      >
        CLINICIAN ASSIST · 2026
      </text>
    </svg>
  );
}

/** Corner ornament SVG used in all four corners of the diploma frame. */
function CornerOrnament({
  position,
}: {
  position: "tl" | "tr" | "bl" | "br";
}): React.JSX.Element {
  // Each ornament is the SAME path, rotated to the four corners.
  const rotate =
    position === "tl"
      ? "rotate(0)"
      : position === "tr"
        ? "rotate(90)"
        : position === "br"
          ? "rotate(180)"
          : "rotate(270)";
  const posClass =
    position === "tl"
      ? "-top-3 -left-3"
      : position === "tr"
        ? "-top-3 -right-3"
        : position === "bl"
          ? "-bottom-3 -left-3"
          : "-bottom-3 -right-3";
  return (
    <svg
      viewBox="0 0 60 60"
      className={`pointer-events-none absolute h-10 w-10 ${posClass}`}
      aria-hidden="true"
    >
      <g transform={`${rotate} ${rotate === "rotate(0)" ? "" : "30 30"}`.trim()}>
        <path
          d="M5 5 L55 5 L55 9 L9 9 L9 55 L5 55 Z"
          fill="#C9A86B"
        />
        <circle cx="15" cy="15" r="4" fill="none" stroke="#C9A86B" strokeWidth="1.5" />
        <circle cx="15" cy="15" r="1.6" fill="#C9A86B" />
        <path d="M22 15 L34 15 M15 22 L15 34" stroke="#C9A86B" strokeWidth="1.2" />
      </g>
    </svg>
  );
}

interface DiplomaProps {
  payload: VerifyPayload;
}

/** The "frameable" certificate artifact — mirrors the PDF layout in
 *  HTML/CSS so a verifier sees the same document on screen as on paper. */
function Diploma({ payload }: DiplomaProps): React.JSX.Element {
  return (
    <div
      className="relative mx-auto w-full max-w-4xl rounded-sm bg-[#FBF8F1] p-2 shadow-[0_30px_60px_-20px_rgba(11,37,69,0.45)] sm:p-3"
      style={{ aspectRatio: "11 / 8.5" }}
    >
      {/* Layered double border — navy outer, gold inner */}
      <div
        className="relative h-full w-full"
        style={{
          border: "6px solid #0B2545",
          padding: "10px",
        }}
      >
        <div
          className="relative h-full w-full"
          style={{
            border: "1.5px solid #C9A86B",
            outline: "1.5px solid #C9A86B",
            outlineOffset: "5px",
            padding: "min(2.6vw, 30px) min(3.8vw, 44px)",
            color: "#0B2545",
          }}
        >
          <CornerOrnament position="tl" />
          <CornerOrnament position="tr" />
          <CornerOrnament position="bl" />
          <CornerOrnament position="br" />

          {/* Header — seal + institution */}
          <header className="flex items-center gap-4 border-b border-[#C9A86B]/60 pb-3 sm:gap-5 sm:pb-4">
            <CaitSeal size={68} />
            <div className="text-left">
              <div
                className="font-serif font-bold leading-none text-[#0B2545]"
                style={{ fontFamily: "Georgia, serif", letterSpacing: "0.06em", fontSize: "clamp(16px, 2.6vw, 28px)" }}
              >
                CaiT Academy
              </div>
              <div
                className="mt-1 uppercase text-gray-500"
                style={{
                  fontFamily: "Helvetica, Arial, sans-serif",
                  letterSpacing: "0.18em",
                  fontSize: "clamp(7px, 1.1vw, 10px)",
                }}
              >
                Clinician Assist Inc.
              </div>
              <div
                className="mt-1 italic text-[#C9A86B]"
                style={{ fontFamily: "Georgia, serif", fontSize: "clamp(9px, 1.3vw, 12px)" }}
              >
                Cognitive Artificial Intelligence in Therapy
              </div>
            </div>
          </header>

          {/* Body — classical phrasing centered */}
          <section className="mt-4 text-center sm:mt-6">
            <div
              className="italic text-[#C9A86B]"
              style={{ fontFamily: "Georgia, serif", letterSpacing: "0.08em", fontSize: "clamp(10px, 1.6vw, 14px)" }}
            >
              — Certificate of Completion —
            </div>
            <div
              className="mt-3 italic text-[#0B2545]"
              style={{ fontFamily: "Georgia, serif", fontSize: "clamp(10px, 1.4vw, 13px)", lineHeight: 1.5 }}
            >
              Be it known to all who shall see these presents that
            </div>
            <h1
              className="mx-auto mt-2 inline-block italic text-[#0B2545]"
              style={{
                fontFamily: "Georgia, 'Times New Roman', serif",
                fontWeight: 400,
                lineHeight: 1.05,
                borderBottom: "0.6px solid #C9A86B",
                padding: "0 24px 6px 24px",
                fontSize: "clamp(28px, 5.4vw, 56px)",
              }}
            >
              {payload.holder_display_name}
            </h1>
            <div
              className="mx-auto mt-3 italic text-[#0B2545]"
              style={{ fontFamily: "Georgia, serif", fontSize: "clamp(10px, 1.4vw, 13px)", lineHeight: 1.5, maxWidth: "44em" }}
            >
              having satisfied the requirements established by the faculty,
              <br />
              is hereby awarded the
            </div>
            <h2
              className="mt-3 uppercase font-bold text-[#0B2545]"
              style={{
                fontFamily: "Helvetica, Arial, sans-serif",
                letterSpacing: "0.22em",
                fontSize: "clamp(14px, 2.4vw, 22px)",
              }}
            >
              {payload.credential_name}
            </h2>
            <p
              className="mx-auto mt-3 text-gray-700"
              style={{
                fontFamily: "'Times New Roman', serif",
                fontSize: "clamp(10px, 1.3vw, 12.5px)",
                lineHeight: 1.55,
                maxWidth: "46em",
              }}
            >
              for foundational competency in the ethical clinical application
              of conversational artificial intelligence in mental-health
              practice, following the CaiT v3.0 framework.
            </p>
            <div
              className="mt-3 text-gray-500"
              style={{ fontFamily: "Georgia, serif", fontStyle: "italic", fontSize: "clamp(9px, 1.2vw, 11px)" }}
            >
              Granted at the office of Clinician Assist Inc. this{" "}
              {formatDate(payload.issued_at)}.
            </div>
          </section>

          {/* Footer — signature block + verify QR */}
          <footer className="mt-4 flex items-end justify-between gap-4 border-t border-[#C9A86B]/60 pt-3 sm:mt-5 sm:pt-4">
            <div className="text-center">
              <div
                className="text-[#0B2545]"
                style={{
                  fontFamily: "'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', cursive",
                  borderBottom: "1px solid #0B2545",
                  paddingBottom: "4px",
                  fontSize: "clamp(20px, 3.4vw, 30px)",
                }}
              >
                Jess Jessop
              </div>
              <div
                className="mt-1 uppercase text-gray-500"
                style={{
                  fontFamily: "Helvetica, Arial, sans-serif",
                  letterSpacing: "0.16em",
                  fontSize: "clamp(7px, 1.05vw, 9px)",
                }}
              >
                Founder · Clinician Assist Inc.
              </div>
            </div>
            <div className="flex items-center gap-3 text-left">
              <img
                src={payload.qr_code_url}
                alt="Verification QR code"
                className="border border-[#C9A86B] bg-white p-[3px]"
                style={{ width: "min(20vw, 80px)", height: "min(20vw, 80px)" }}
              />
              <div className="max-w-[22em]">
                <div
                  className="uppercase text-gray-500"
                  style={{
                    fontFamily: "Helvetica, Arial, sans-serif",
                    letterSpacing: "0.18em",
                    fontSize: "clamp(7px, 1.05vw, 9px)",
                  }}
                >
                  Verify online
                </div>
                <div
                  className="mt-1 font-mono text-[#0B2545]"
                  style={{ fontSize: "clamp(7px, 1.05vw, 9.5px)", wordBreak: "break-all" }}
                >
                  app.cait.academy/verify/{payload.credential_id.slice(0, 8)}…
                </div>
                <div
                  className="mt-1 font-mono text-gray-500"
                  style={{ fontSize: "clamp(6.5px, 1vw, 9px)", wordBreak: "break-all" }}
                >
                  ID {payload.credential_id}
                </div>
              </div>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
}

export function VerifyPage(): React.JSX.Element {
  const { credentialId = "" } = useParams<{ credentialId: string }>();
  const [load, setLoad] = useState<LoadState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    if (!credentialId) {
      setLoad({ kind: "error", message: "Missing credential ID in URL." });
      return;
    }
    if (!UUID_RE.test(credentialId)) {
      setLoad({ kind: "not_found" });
      return;
    }
    void (async () => {
      try {
        const payload = await fetchCredential(credentialId);
        if (cancelled) return;
        setLoad({ kind: "loaded", payload });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setLoad({ kind: "not_found" });
          return;
        }
        const msg = err instanceof Error ? err.message : "Verification failed.";
        setLoad({ kind: "error", message: msg });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [credentialId]);

  if (load.kind === "loading") {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6">
        <p className="text-muted-foreground">Verifying credential…</p>
      </div>
    );
  }

  if (load.kind === "not_found") {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6">
        <h1 className="text-2xl font-semibold text-foreground">
          Credential not found
        </h1>
        <p className="mt-4 text-muted-foreground">
          No public record exists for this credential ID. The ID may be
          mistyped, or the credential may have been removed. If the
          credential holder believes this is a mistake, ask them to contact
          CaiT Academy support with the credential ID below.
        </p>
        <p className="mt-6 font-mono text-xs text-muted-foreground">
          {credentialId}
        </p>
      </div>
    );
  }

  if (load.kind === "error") {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6">
        <h1 className="text-2xl font-semibold text-foreground">
          Verification unavailable
        </h1>
        <p className="mt-4 text-muted-foreground">{load.message}</p>
      </div>
    );
  }

  const { payload } = load;
  const badge = statusBadge(payload);

  return (
    <div
      className="min-h-screen px-4 py-10 sm:px-6"
      style={{
        background:
          "radial-gradient(ellipse at top, #F4EFE1 0%, #E8E2D2 100%)",
      }}
    >
      <div className="mx-auto max-w-5xl">
        <div className="mb-6 text-center">
          <p
            className="uppercase text-gray-500"
            style={{ fontFamily: "Helvetica, Arial, sans-serif", letterSpacing: "0.22em", fontSize: "11px" }}
          >
            CaiT Academy — Credential Verification
          </p>
          <p
            className="mt-2 text-[#0B2545]"
            style={{ fontFamily: "Georgia, serif", fontStyle: "italic", fontSize: "15px" }}
          >
            The certificate below has been verified against the issuer's records.
          </p>
        </div>

        {/* The diploma artifact */}
        <Diploma payload={payload} />

        {/* Verification panel below the artifact */}
        <section className="mx-auto mt-10 max-w-3xl rounded-xl bg-white p-6 shadow-sm sm:p-8">
          <div className={`flex items-center gap-3 rounded-lg border px-4 py-3 ${badge.pillClass}`}>
            <div className="text-base font-semibold">{badge.label}</div>
            <div className="text-sm">{badge.description}</div>
          </div>

          <dl className="mt-6 grid gap-5 sm:grid-cols-3">
            <div>
              <dt className="text-xs uppercase tracking-wider text-gray-500">
                Credential type
              </dt>
              <dd className="mt-1 text-foreground">{payload.credential_type}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wider text-gray-500">
                Issued
              </dt>
              <dd className="mt-1 text-foreground">{formatDate(payload.issued_at)}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wider text-gray-500">
                Recertify by
              </dt>
              <dd className="mt-1 text-foreground">{formatDate(payload.expires_at)}</dd>
            </div>
            <div className="sm:col-span-3">
              <dt className="text-xs uppercase tracking-wider text-gray-500">
                Credential ID
              </dt>
              <dd className="mt-1 break-all font-mono text-sm text-foreground">
                {payload.credential_id}
              </dd>
            </div>
          </dl>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <a
              href={payload.pdf_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-md bg-[#0B2545] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#0B2545]/90"
            >
              Download original PDF
            </a>
            <a
              href={payload.qr_code_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-md border border-[#C9A86B] px-5 py-2.5 text-sm font-semibold text-[#0B2545] hover:bg-[#FBF8F1]"
            >
              View QR code
            </a>
          </div>
        </section>

        <footer className="mt-10 text-center text-xs text-gray-500">
          <p>
            Verification served from CaiT Academy ({" "}
            <a href="https://cait.academy" className="underline">
              cait.academy
            </a>
            ). CaiT Foundations is an education-only credential; it does not
            authorize independent clinical practice or production Casey use
            (D18).
          </p>
        </footer>
      </div>
    </div>
  );
}
